import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import { safeFromUnixTime } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { GetHelpModalTypes } from '@/components/Modals/GetHelp/enums/GetHelpModal.enum';
import Panel from '@/components/Panel/Panel';
import { RouteButton } from '@/components/RouteButton/RouteButton';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { ShippingMethods } from '@/enums/shippingMethod';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import WonItemLocation from '@/pages/WonItems/components/WonItemsCard/components/WonItemLocation/WonItemLocation';
import WonItemsCardHeader from '@/pages/WonItems/components/WonItemsCard/components/WonItemsHeader/WonItemsCardHeader';
import { shouldAllowReview, shouldDelayReview } from '@/pages/WonItems/components/WonItemsCard/helpers/reviews.util';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchCatalogDetailsIfNeeded } from '@/redux/modules/catalog/detail/catalogDetail.actions';
import { reopenPaidInvoice } from '@/redux/modules/checkout/checkout.actions';
import { openGetHelpModal, openLiveShippingDeadlineModal } from '@/redux/modules/modal';
import { fetchPurchasedShippingQuote } from '@/redux/modules/shippingQuote/shippingQuote.actions';
import { getShippingQuote } from '@/redux/modules/shippingQuote/shippingQuote.selectors';
import { getWonCatalogItemsByLocations } from '@/redux/modules/wonItems/wonItems.selectors';
import { WonCatalog } from '@/types/WonCatalog';
import { WonItemInfo } from '@/types/WonItem';
import { getCheckoutUrl, getCheckoutUrlATG, getReviewUrl } from '@/utils/urls';

import StatusBadge from './components/StatusBadge/StatusBadge';
import WonItemRow from './components/WonItemRow/WonItemRow';
import WonItemsLinks from './components/WonItemsLinks/WonItemsLinks';
import { GetWonCatalogStatusParams, useGetWonCatalogStatus } from './hooks/useGetWonCatalogStatus';

export type WonItemsCardProps = {
    isBidderSuspended: boolean;
    reviewed: boolean;
    wonCatalog: WonCatalog;
};

const WonItemsCard = ({ isBidderSuspended, reviewed, wonCatalog }: WonItemsCardProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isMobile = useIsNarrowerThan(theme.breakpoints.tabletNarrowWidthNum + 1);

    const [loading, setLoading] = useState(false);

    const {
        assumePaid,
        atgpay,
        buyersPremium,
        catalogId,
        currency,
        eligibleShipping,
        invoice,
        items,
        liveAuctioneersPayments,
        paymentStatus,
        saleStartTs,
        sellerId,
        sellerName,
        title,
    } = wonCatalog;
    const itemsByLocation = useAppSelector((state) => getWonCatalogItemsByLocations(state, wonCatalog.catalogId));

    const { shippingMethod, shippingStatus, shippingStatusSecondary, shippingTrackingLink } =
        useAppSelector((state) => getShippingQuote(state, catalogId)) || {};

    const { invoiceId, invoiceTotal, lastPaymentDate, paidAmount, paidStatus, sent } = invoice;
    const { limitToAddDate, quoteStatus, shippingAvailable } = eligibleShipping || {};
    const saleStart = safeFromUnixTime(saleStartTs);

    const isDue = paidStatus === 'due';
    const isPaid = paidStatus === 'paid';
    const isLiveShipping = shippingMethod === ShippingMethods.LiveShipping;

    const isQuotePurchased = quoteStatus === 'purchased' || (isLiveShipping && isPaid);
    const isQuoteUnpurchased = quoteStatus === 'unpurchased' || (!isLiveShipping && isPaid);
    const shippingAddressId = eligibleShipping?.shippingAddress?.addressId;

    const totalPrice: number = isDue ? invoiceTotal : paidAmount;
    const withinAddShippingDeadline = limitToAddDate?.getTime() > Date.now();

    const canAddLiveShipping =
        isPaid &&
        shippingAvailable &&
        Boolean(quoteStatus) &&
        !isQuotePurchased &&
        isQuoteUnpurchased &&
        withinAddShippingDeadline &&
        Boolean(shippingAddressId);

    const canCheckout = !isPaid && sent && liveAuctioneersPayments && !assumePaid && paidAmount === 0;

    useEffect(() => {
        if (invoiceId) {
            dispatch(fetchPurchasedShippingQuote({ catalogId, invoiceId }));
        }
    }, [catalogId, dispatch, invoiceId, isPaid, isQuotePurchased, wonCatalog]);

    const displayLiveShippingDeadlineModal = () => {
        dispatch(openLiveShippingDeadlineModal());
    };

    const reopenInvoice = useCallback(async () => {
        setLoading(true);
        await dispatch(reopenPaidInvoice({ catalogId, invoiceId, shippingAddressId })).then(() =>
            atgpay ? navigate(getCheckoutUrlATG(catalogId)) : navigate(getCheckoutUrl(catalogId))
        );
        setLoading(false);
    }, [atgpay, catalogId, dispatch, shippingAddressId, invoiceId, navigate]);

    const allowedReview = shouldAllowReview(paidStatus, reviewed, saleStart, isBidderSuspended) && (
        <RouteLink
            size="sm"
            to={getReviewUrl(catalogId)}
        >
            <FormattedMessage id="write_review" />
        </RouteLink>
    );

    const reviewLink = shouldDelayReview(saleStart, isBidderSuspended) ? null : allowedReview;
    const catalogItems: WonItemInfo[] = items.map((item) => ({
        ...item,
        catalogId,
        currency,
        sellerId,
    }));

    let statusParams: GetWonCatalogStatusParams = {
        catalogId,
        invoiceId,
        lastPaymentDate,
        primaryStatus: paymentStatus,
        secondaryStatus: isQuotePurchased ? shippingStatusSecondary : undefined,
        shippingMethod: isDue ? undefined : shippingMethod,
    };
    if (canAddLiveShipping || isQuotePurchased) {
        statusParams = {
            ...statusParams,
            addShippingDate: limitToAddDate,
            primaryStatus: shippingStatus,
            secondaryStatus: shippingStatusSecondary,
            shippingAmount: Number(eligibleShipping?.shippingQuote?.total),
            shippingAvailable: shippingAvailable && isQuoteUnpurchased,
            shippingCurrency: eligibleShipping?.shippingQuote ? eligibleShipping.shippingQuote.currency : undefined,
            shippingMethod: isQuotePurchased ? shippingMethod : undefined,
            trackingLink: shippingTrackingLink,
        };
    } else if (Boolean(shippingStatus) && !isDue) {
        statusParams = {
            ...statusParams,
            primaryStatus: shippingStatus,
            secondaryStatus: shippingStatusSecondary,
            shippingMethod,
        };
    } else if (isDue) {
        statusParams = {
            ...statusParams,
            primaryStatus: paymentStatus,
            secondaryStatus: undefined,
            shippingMethod: undefined,
        };
    }
    const { primaryText, secondaryText } = useGetWonCatalogStatus(statusParams);

    const statusBadge = (
        <StatusBadge
            primaryText={primaryText}
            secondaryText={secondaryText}
            shippingMethod={isQuotePurchased ? shippingMethod : undefined}
            trackingLink={shippingTrackingLink}
        />
    );

    let wonItems: JSX.Element;
    const hasMultipleLocations = Object.keys(itemsByLocation).length > 1;
    let hasSingleStatus = true;
    // has multiple locations, the keys of the object are the addresses of house locations
    if (hasMultipleLocations) {
        hasSingleStatus = Object.keys(itemsByLocation).reduce(
            (isSingleStatusSoFar, hash) => isSingleStatusSoFar && !itemsByLocation[hash].shippingStatus,
            true
        );
        wonItems = (
            <StyledItemsContainer $hasSingleStatus={hasSingleStatus}>
                {Object.keys(itemsByLocation).map((locationHash, index) => {
                    return (
                        <span key={locationHash}>
                            <WonItemLocation
                                locationInfo={itemsByLocation[locationHash]}
                                wonCatalog={wonCatalog}
                            />
                            {index < Object.keys(itemsByLocation).length - 1 && <StyledDivider />}
                        </span>
                    );
                })}
            </StyledItemsContainer>
        );
    } else {
        // only has a single location, so no need to separate them into sections
        wonItems = (
            <StyledItemsContainer $hasSingleStatus>
                {catalogItems.map((item) => (
                    <WonItemRow
                        buyersPremium={buyersPremium}
                        item={item}
                        key={item.itemId}
                        won
                    />
                ))}
            </StyledItemsContainer>
        );
    }

    let wonItemCardButton: JSX.Element = null;

    if (canCheckout) {
        wonItemCardButton = (
            <RouteButton to={atgpay ? getCheckoutUrlATG(catalogId) : getCheckoutUrl(catalogId)}>
                <FormattedMessage id="wonitems.checkout" />
            </RouteButton>
        );
    } else if (canAddLiveShipping) {
        wonItemCardButton = (
            <Button
                aria-label="Add Shipping"
                loading={loading}
                onClick={!withinAddShippingDeadline ? displayLiveShippingDeadlineModal : reopenInvoice}
            >
                <FormattedMessage id="wonitems.addShipping" />
            </Button>
        );
    }

    const displayGetHelpModal = async () => {
        await dispatch(fetchCatalogDetailsIfNeeded([catalogId]));
        dispatch(
            openGetHelpModal({
                data: {
                    modalData: {
                        catalogId,
                        invoiceId,
                        invoiceStatus: primaryText,
                        isLivePayments: liveAuctioneersPayments,
                        isLiveShipping,
                        isPaid: isPaid || assumePaid,
                        itemCount: items.length,
                        itemId: items[0].itemId,
                        sellerId,
                    },
                    modalVariant: GetHelpModalTypes.WonItemsPage,
                },
            })
        );
    };

    const wonCatalogLinks = (
        <WonItemsLinks
            atgPayActive={Boolean(atgpay)}
            bidderIsSuspended={isBidderSuspended}
            catalogId={catalogId}
            invoiceId={invoiceId}
            isLivePayments={liveAuctioneersPayments}
            paidStatus={paidStatus}
            reviewed={reviewed}
            reviewLink={reviewLink}
            sent={sent}
            showGetHelpModal={displayGetHelpModal}
        />
    );

    return (
        <Card
            header={
                <WonItemsCardHeader
                    currency={currency}
                    date={saleStartTs}
                    invoiceId={invoiceId}
                    sellerName={sellerName}
                    title={title}
                    totalPrice={totalPrice}
                />
            }
            key={catalogId}
        >
            <Body>
                <StyledLeftSide>
                    {hasSingleStatus && (
                        <>
                            <BadgeContainer>{statusBadge}</BadgeContainer>
                            <ButtonContainer>{wonItemCardButton}</ButtonContainer>
                        </>
                    )}
                    {isMobile && <StyledMobileCatalogLinks>{wonCatalogLinks}</StyledMobileCatalogLinks>}
                    {wonItems}
                </StyledLeftSide>
                {!isMobile && <StyledDesktopCatalogLinks>{wonCatalogLinks}</StyledDesktopCatalogLinks>}
            </Body>
        </Card>
    );
};

export default WonItemsCard;

const Card = styled(Panel)`
    box-shadow: none;
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    background-color: ${({ theme }) => theme.colors.white};
    padding: 0;
    margin-top: 32px;
`;

const Body = styled.div`
    margin: 32px;
    display: flex;
    justify-content: space-between;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: block;
        margin: 32px 24px;
    }
`;

const BadgeContainer = styled.div`
    display: flex;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        max-width: 95%;
        margin-top: 32px;
    }
`;

const StyledLeftSide = styled.section`
    display: block;
`;

const ButtonContainer = styled.section`
    margin-top: 16px;
    a,
    button {
        max-width: 190px;
        width: 100%;
    }
`;

const StyledItemsContainer = styled.section<{ $hasSingleStatus?: boolean }>`
    margin-top: ${({ $hasSingleStatus }) => ($hasSingleStatus ? '32px' : 0)};
    display: flex;
    flex: 2;
    flex-direction: column;
`;

const StyledMobileCatalogLinks = styled.div`
    margin: 32px 0;
`;

const StyledDesktopCatalogLinks = styled.section`
    display: flex;
    flex-direction: column;
    text-align: right;
    margin-left: min(32px);
`;

const StyledDivider = styled.div`
    margin: 32px 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    width: 905px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 100%;
    }
`;
